import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { getStartTimeSeconds } from '@mimir/client-common/model/playableItem';
import { getDropFrameTimecodeDisplayFormat } from '@mimir/client-common/util/premiere';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { storeDispatchLoadStorageProfile } from '@mimir/web/desktop/storageProfile';
import type { DownloadAndRelinkOps } from '@mimir/client-common/fileSync/downloadAndRelinkOps';
import type { DownloadPlan } from '@mimir/client-common/fileSync/downloadIdentity';
import { syncItemFieldList, type SyncItemInfo } from '@mimir/client-common/fileSync/premiereTypes';
import type { BatchImportNotifier } from '@mimir/client-common/model/batchImportNotifier';
import type { OngoingRestoreStatus } from '@mimir/client-common/model/ongoingDownload';
import { exceptionAnalysis, exceptionSummary } from '@mimir/common/util/exception';
import { getItem } from '../../util/api';
import { importClip } from '../../premiere/importClip';
import { showToast, TOAST } from '@mimir/web/eventBus';
import type { ColdStorageRestoreOps } from '@mimir/client-common/coldStorage/coldStorageRestoreOps';


export class DefaultDownloadAndRelinkOps implements DownloadAndRelinkOps {

  constructor(
    private applicationContext: AdobePanelContext | ElectronContext,
    private notificationOps: NotificationOps,
    private notifier: BatchImportNotifier | undefined,
    private logger: Logger,
    private ongoingDownloadOps: OngoingDownloadOps,
    private localStateOps: LocalStateOps,
    private coldStorageRestoreOps: ColdStorageRestoreOps,
    private downloadPath: string,
    private userDataSystemPath: string,
    private downloadToSubfolderPaths: boolean,
    private shouldUsePremierePreferences: boolean,
    private skippedFileExtensions: string[],
  ) {}


  async downloadAndRelink(
    isProxyMode: boolean,
    isStandaloneMode: boolean,
    plan: DownloadPlan,
    prefetched: Record<string, SyncItemInfo> | null,
    ongoingRestoreStatus: OngoingRestoreStatus | undefined,
  ): Promise<void> {
    const services = {
      applicationContext: this.applicationContext,
      notificationOps: this.notificationOps,
      notifier: this.notifier,
      logger: this.logger,
      ongoingDownloadOps: this.ongoingDownloadOps,
      localStateOps: this.localStateOps,
      coldStorageRestoreOps: this.coldStorageRestoreOps,
    };
    const config = {
      isProxyMode,
      isStandaloneMode,
      downloadPath: this.downloadPath,
      userDataSystemPath: this.userDataSystemPath,
      downloadToSubfolderPaths: this.downloadToSubfolderPaths,
      shouldUsePremierePreferences: this.shouldUsePremierePreferences,
      skippedFileExtensions: this.skippedFileExtensions,
    };
    await downloadAndRelink(services, config, plan, prefetched, ongoingRestoreStatus);
  }

}


async function downloadAndRelink(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    notifier: BatchImportNotifier | undefined,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
  },
  config: {
    isProxyMode: boolean,
    isStandaloneMode: boolean,
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  plan: DownloadPlan,
  prefetched: Record<string, SyncItemInfo> | null,
  ongoingRestoreStatus: OngoingRestoreStatus | undefined,
): Promise<void> {
  try {
    let item: SyncItemInfo | undefined;
    const itemId = plan.dlId.itemId;
    if (prefetched) {
      item = prefetched[itemId];
    }

    if (!item) {
      const queryParams = {
        fields: syncItemFieldList.join(','),
      };
      item = await getItem(itemId, queryParams);
    }

    if (!item || !item.metadata) {
      services.logger.error(`item could not be fetched: ${itemId}`);
      showToast('Error downloading a missing media.', TOAST.ERROR);
      return;
    }

    const validProfile = await storeDispatchLoadStorageProfile(
      services.applicationContext,
      false,
      config.downloadPath,
      services.localStateOps,
    );
    if (!validProfile) {
      return; // User cancelled.
    }

    await importClip(services, { ...config, downloadPath: validProfile.downloadPath, isProxyMode: validProfile.isProxyWorkflow }, {
      originItem: item,
      storageParams: validProfile,
      ongoingRestoreStatus,
      shouldRefreshExistingClip: true,
      plan,
      startTimeSeconds: getStartTimeSeconds(services.logger, item),
      timeDisplayFormat: getDropFrameTimecodeDisplayFormat(item),
      timelineInsertion: null,
    });
  } catch (err) {
    services.logger.error(`downloadAndRelink: ${exceptionAnalysis(err)}`);
    showToast(`Error while downloading clip: ${exceptionSummary(err)}`, TOAST.ERROR);
  }
}
